import { ProductPageShell } from "@/components";
import { Building2Icon } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { resolvePortalBrandImage } from "../lib/branding";
import { useTenant } from "../tenant/TenantContext";

type PortalBrandProps = {
  name: string;
  image?: string | null;
  compact?: boolean;
  navbar?: boolean;
};

type PortalPageHeaderProps = {
  title: string;
  description?: string;
  meta?: ReactNode;
  actions?: ReactNode;
};

type PortalAuthCardProps = {
  eyebrow?: string;
  title: string;
  description?: string;
  children: ReactNode;
};

/**
 * Renders the tenant-owned identity used throughout the public and authenticated portal.
 */
export const PortalBrand = ({ name, image, compact = false, navbar = false }: PortalBrandProps) => {
  const imageUrl = resolvePortalBrandImage(image);
  const [imageFailed, setImageFailed] = useState(false);
  const hasVisibleImage = Boolean(imageUrl && !imageFailed);
  const usesNavbarLogo = navbar && hasVisibleImage;
  const sizeClass = usesNavbarLogo ? "h-14 max-w-52" : compact ? "h-9 w-9 rounded-lg" : "h-12 w-12 rounded-xl";

  useEffect(() => setImageFailed(false), [imageUrl]);

  return (
    <div className={`flex min-w-0 items-center ${usesNavbarLogo ? "" : "gap-3"}`}>
      <span
        className={`flex shrink-0 items-center justify-center overflow-hidden font-semibold text-[#131B2A] ${usesNavbarLogo ? "" : "border border-gray-200 bg-white shadow-[0_1px_2px_rgba(19,27,42,0.05)]"} ${sizeClass}`}
      >
        {hasVisibleImage ? (
          <img
            src={imageUrl}
            alt={`${name} logo`}
            className={usesNavbarLogo ? "h-full max-w-full object-contain" : "h-full w-full object-contain"}
            onError={() => setImageFailed(true)}
          />
        ) : (
          <Building2Icon className={compact ? "h-4 w-4" : "h-5 w-5"} strokeWidth={1.7} aria-hidden="true" />
        )}
      </span>
      {!usesNavbarLogo ? (
        <span className="min-w-0">
          <span className={`block truncate font-semibold text-[#131B2A] ${compact ? "text-sm" : "text-base"}`}>
            {name}
          </span>
          <span className="block text-[11px] font-medium text-gray-500">Partner portal</span>
        </span>
      ) : null}
    </div>
  );
};

/**
 * Provides the standard compact product canvas for authenticated portal pages.
 */
export const PortalPageShell = ({ children, width = "max-w-[1800px]" }: { children: ReactNode; width?: string }) => (
  <ProductPageShell density="compact" contentClassName={width} className="!bg-(--portal-canvas) !border-t-0">
    {children}
  </ProductPageShell>
);

/**
 * Renders the compact heading pattern shared by portal workspace pages.
 */
export const PortalPageHeader = ({ title, description, meta, actions }: PortalPageHeaderProps) => (
  <header className="flex min-h-11 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <h1 className="portal-title text-2xl font-semibold text-[#131B2A]">{title}</h1>
        {meta ? <span className="text-xs font-medium tabular-nums text-gray-500">{meta}</span> : null}
      </div>
      {description ? <p className="mt-0.5 hidden truncate text-xs text-gray-500 lg:block">{description}</p> : null}
    </div>
    {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
  </header>
);

/** Provides the branded public shell used by secondary portal authentication flows. */
export const PortalAuthCard = ({ eyebrow = "Agent portal", title, description, children }: PortalAuthCardProps) => {
  const tenant = useTenant();
  return (
    <main className="min-h-[100dvh] bg-[#FBFBFB] text-[#131B2A]">
      <header className="border-b border-gray-200 bg-white">
        <nav aria-label="Portal navigation" className="mx-auto flex h-16 max-w-7xl items-center px-4 sm:px-6 lg:px-8">
          {tenant.tenantName ? (
            <PortalBrand name={tenant.tenantName} image={tenant.branding?.logo} compact navbar />
          ) : (
            <PortalBrand name="Partner portal" compact navbar />
          )}
        </nav>
      </header>
      <div className="grid min-h-[calc(100dvh-4rem)] place-items-center px-5 py-10 sm:px-8">
        <section className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-6 shadow-[0_12px_32px_rgba(19,27,42,0.08)] sm:p-8">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-(--brand-primary)">{eyebrow}</p>
          <h1 className="mt-2 text-2xl font-semibold tracking-tight text-[#131B2A]">{title}</h1>
          {description ? <p className="mt-2 text-sm leading-6 text-gray-500">{description}</p> : null}
          {children}
        </section>
      </div>
    </main>
  );
};
